import { Route, Widget } from "widgets-for-react"
import { ApplicationState } from "../../../EntryPoint/Frontend/allIncludedModules"
import { CurrentPage, RouteBuilders, Routes, RouteUpdaters } from "../utils/routes.shared"
import { Updater } from "widgets-for-react";
import {  StandardLocalWidget  } from "widgets-for-react"
// import { AuthState } from "../../../Modules/Shoppinger2.Authentication/Frontend/Authentication/root/authState"

export type ExtendedModuleState<moduleState extends {}, allApplicationPages> = 
  moduleState & { page: CurrentPage<allApplicationPages> }

export type Module<modulePages, moduleState extends {}, applicationStateRequirements, applicationPagesRequirements> = {
  new: <allApplicationState extends moduleState & applicationStateRequirements>() => {
    routeBuilders:() => RouteBuilders<moduleState, modulePages>,
    routeUpdaters:() => 
      RouteUpdaters<ExtendedModuleState<allApplicationState & moduleState & applicationStateRequirements, modulePages & applicationPagesRequirements>, modulePages & applicationPagesRequirements, keyof modulePages>,
    routeWidgetsObject: () => 
      Routes<ExtendedModuleState<allApplicationState & moduleState & applicationStateRequirements, modulePages & applicationPagesRequirements>, modulePages & applicationPagesRequirements, keyof modulePages>
    routeWidgets: () => 
      Route<Updater<ExtendedModuleState<allApplicationState & moduleState & applicationStateRequirements, modulePages & applicationPagesRequirements>>>[]
    tryPageSwitch: (currentState: 
      ExtendedModuleState<allApplicationState & moduleState & applicationStateRequirements, modulePages & applicationPagesRequirements>) => Widget<Updater<ExtendedModuleState<allApplicationState & moduleState & applicationStateRequirements, modulePages & applicationPagesRequirements>>> | undefined,
    headerWidget: (currentState: 
      ExtendedModuleState<allApplicationState & moduleState & applicationStateRequirements, modulePages & applicationPagesRequirements>) => Widget<Updater<ExtendedModuleState<allApplicationState & moduleState & applicationStateRequirements, modulePages & applicationPagesRequirements>>>,
  },
  initialState:() => moduleState
}

export type ApplicationBuilder<applicationPages = {}, applicationState = {}> = {
  addModule: <modulePages, moduleState extends {}>(module:Module<modulePages, moduleState, applicationState, applicationPages>) => 
    ApplicationBuilder<applicationPages & modulePages, applicationState & moduleState>,
  routeBuilders:() => RouteBuilders<applicationState, applicationPages>,
  routeUpdaters:<allApplicationState extends applicationState, allApplicationPages extends applicationPages>() => 
    RouteUpdaters<allApplicationState & applicationState, allApplicationPages & applicationPages, keyof applicationPages>,
  routeWidgetsObject: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>() => 
    Routes<allApplicationState & applicationState, allApplicationPages & applicationPages, keyof applicationPages>
  routeWidgets: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>() => 
    Route<Updater<ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>>>[]
  tryPageSwitch: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>(currentState: 
    ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>) => 
    Widget<Updater<ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>>> | undefined,
  headerWidgets: <allApplicationState extends applicationState, allApplicationPages extends applicationPages>(currentState: 
    ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>) => 
    Widget<Updater<ExtendedModuleState<allApplicationState & applicationState, allApplicationPages & applicationPages>>>[],
  initialState:() => applicationState
}

export const applicationBuilder : ApplicationBuilder = {
  addModule:function<modulePages, moduleState extends {}>(this:ApplicationBuilder, nextModule:Module<modulePages, moduleState, {}, {}>) {
    return addModule<{}, {}, modulePages, moduleState>(this, nextModule)
  },
  routeBuilders:() => ({}),
  routeUpdaters:() => ({}),
  routeWidgetsObject:() => ({}),
  routeWidgets:() => [],
  tryPageSwitch:({}) => undefined,
  headerWidgets:() => [],
  initialState:() => ({})
}

function addModule<currentRoutePages, currentApplicationState, additionalRoutePages, additionalModuleState>(
    application:ApplicationBuilder<currentRoutePages, currentApplicationState>,
    module:Module<additionalRoutePages, additionalModuleState, currentApplicationState, currentRoutePages>) 
  : ApplicationBuilder<currentRoutePages & additionalRoutePages, currentApplicationState & additionalModuleState> {
  type LocalAppBuilder = ApplicationBuilder<currentRoutePages & additionalRoutePages, currentApplicationState & additionalModuleState>
  type LocalRoutePages = currentRoutePages & additionalRoutePages
  type LocalAppState = currentApplicationState & additionalModuleState
  return {
    addModule:function<modulePages, moduleState extends {}>(this:LocalAppBuilder, nextModule:Module<modulePages, moduleState, currentApplicationState, currentRoutePages>) {
      return addModule<LocalRoutePages, LocalAppState, modulePages, moduleState>(this, nextModule as any)
    },
    routeBuilders:function(this:LocalAppBuilder) { return {...application.routeBuilders(), ...module.new().routeBuilders()} as RouteBuilders<LocalAppState, LocalRoutePages> },
    routeUpdaters:function<allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(this:LocalAppBuilder) { 
      return {
        ...application.routeUpdaters<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(), 
        ...module.new<allApplicationState & LocalAppState>().routeUpdaters()
      } as RouteUpdaters<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages> 
    },
    routeWidgetsObject:function<allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(this:LocalAppBuilder) { 
      return {
        ...application.routeWidgetsObject<allApplicationState & LocalAppState, allApplicationPages>(), 
        ...module.new<allApplicationState & LocalAppState>().routeWidgetsObject()
      } 
    },
    routeWidgets:function<allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(
      this:LocalAppBuilder) : Route<Updater<ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>>>[]{ 
        return [
          ...application.routeWidgets<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(), 
          ...module.new<allApplicationState & LocalAppState>().routeWidgets() as any as Route<Updater<ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>>>[] // this is actually safe but TypeScript has some trouble with the types
        ] 
      },
    tryPageSwitch:function<allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(this:LocalAppBuilder, 
      currentState: ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>) { 
      const res1 = application.tryPageSwitch<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(currentState)
      if (res1 != undefined) return res1
      const res2 = module.new<allApplicationState & LocalAppState>().tryPageSwitch(currentState as any) // this is actually safe but TypeScript has some trouble with the types
      if (res2 != undefined) return res2 as any // this is actually safe but TypeScript has some trouble with the types
      return undefined
    },
    headerWidgets:function<allApplicationState extends LocalAppState, allApplicationPages extends LocalRoutePages>(this:LocalAppBuilder,
      currentState: ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>) : Widget<Updater<ExtendedModuleState<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>>>[] {
      return [
        ...application.headerWidgets<allApplicationState & LocalAppState, allApplicationPages & LocalRoutePages>(currentState),
        module.new<allApplicationState & LocalAppState>().headerWidget(currentState as any) as any // this is actually safe but TypeScript has some trouble with the types
      ]
    },
    initialState:function(this:LocalAppBuilder) : LocalAppState { return {...application.initialState(), ...module.initialState()} },
  }
}

export type GetApplicationState<applicationBuilder> = 
  applicationBuilder extends ApplicationBuilder<infer rs, infer as> ? as : "Given type applicationBuilder does not extend ApplicationBuilder"

export type GetApplicationPages<applicationBuilder> = 
  applicationBuilder extends ApplicationBuilder<infer rs, infer as> ? rs : "Given type applicationBuilder does not extend ApplicationBuilder"
